import React from "react";
import { TableSkeleton } from "../common/skeletons";
import { getFilterLabel } from "./getFilterLabel";

const TH_CLASS =
  "px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap";
const TD_CLASS = "px-3 sm:px-6 py-4 whitespace-nowrap text-xs md:text-sm";

export function getActionBadgeColor(action) {
  switch (action) {
    case "purchased":
      return "bg-green-100 text-green-800";
    case "extended":
      return "bg-blue-100 text-blue-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "admin_granted":
      return "bg-violet-100 text-violet-800";
    case "expired":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BoostHistoryTable({
  items,
  isLoading,
  error,
  onRetry,
}) {
  if (isLoading) {
    return <TableSkeleton rows={8} cols={11} />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium flex-1">{error}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg transition-colors">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!items?.length) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs md:text-sm text-gray-500">No boost history found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className={TH_CLASS}>ID</th>
            <th className={TH_CLASS}>User</th>
            <th className={TH_CLASS}>Boost ID</th>
            <th className={TH_CLASS}>Action</th>
            <th className={TH_CLASS}>Duration</th>
            <th className={TH_CLASS}>Radius</th>
            <th className={TH_CLASS}>Start Time</th>
            <th className={TH_CLASS}>End Time</th>
            <th className={TH_CLASS}>Transaction ID</th>
            <th className={TH_CLASS}>Customer ID</th>
            <th className={TH_CLASS}>Created At</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50 transition-colors">
              <td className={`${TD_CLASS} text-gray-900 font-medium`}>
                {item.id}
              </td>
              <td className={TD_CLASS}>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {item.firstName} {item.lastName}
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[180px] sm:max-w-none">
                  {item.email}
                </div>
                <div className="text-xs text-gray-400">{item.contactNo}</div>
                <div className="text-xs text-gray-400">User ID: {item.user_id}</div>
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {item.boost_id ?? "-"}
              </td>
              <td className={TD_CLASS}>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadgeColor(
                    item.action
                  )}`}>
                  {getFilterLabel("action", item.action)}
                </span>
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {Number(item.duration_minutes) >= 52560000
                  ? "Lifetime"
                  : `${item.duration_minutes} min`}
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {item.radius_km} km
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {formatDate(item.start_at)}
              </td>
              <td className={`${TD_CLASS} text-gray-900`}>
                {formatDate(item.end_at)}
              </td>
              <td className={`${TD_CLASS} text-gray-500 font-mono text-xs truncate max-w-[140px]`} title={item.transaction_id}>
                {item.transaction_id || "-"}
              </td>
              <td className={`${TD_CLASS} text-gray-500 font-mono text-xs truncate max-w-[140px]`} title={item.customer_id}>
                {item.customer_id || "-"}
              </td>
              <td className={`${TD_CLASS} text-gray-500`}>
                {formatDate(item.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
